"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ParameterImportanceData, StudyResults } from "@/types/api";
import { logger } from "@/utils/logger";
import {
  getParameterDisplayName,
  isBooleanParameter,
} from "@/utils/parameterNames";
import {
  Activity,
  AlertCircle,
  Brain,
  Filter,
  GitBranch,
  Info,
  TrendingUp,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";

// Analysis types from ResultsEnhanced
interface ParameterStats {
  name: string;
  min: number;
  max: number;
  avg: number;
  stdDev: number;
  values: number[];
  convergenceScore: number;
}

interface AnalysisResult {
  paramStats: Record<string, ParameterStats>;
  performanceProgression: Array<{
    rank: number;
    value: number;
    improvement: number;
  }>;
  parameterImportance: Array<{
    name: string;
    importance: number;
    avgValue: number;
    range: number;
    correlation: number;
    rankCorrelation: number;
    varianceContribution: number;
    permutationImportance: number;
  }>;
  convergenceRate: number;
}

// Define importance calculation methods
type ImportanceMethod =
  | "fanova"
  | "mdi"
  | "permutation"
  | "hybrid"
  | "pruningAware";

interface ImportanceRow {
  name: string;
  displayName: string;
  fanova: number;
  mdi: number;
  permutation: number;
  hybrid: number;
  pruningAware: number;
  confidence: number;
  confidenceInterval: [number, number];
  pruningRate: number;
  convergenceScore: number;
  interactionStrength: number;
  pruningStats?: {
    pruning_rate: number;
    n_completed: number;
    n_pruned: number;
    completed_mean?: number;
    completed_std?: number;
    pruned_mean?: number;
    pruned_std?: number;
  };
}

interface ParameterImportanceEnhancedProps {
  results: StudyResults;
  analysis: AnalysisResult | null;
}

const IMPORTANCE_METHODS: Record<
  ImportanceMethod,
  { label: string; description: string; icon: React.ReactNode }
> = {
  fanova: {
    label: "fANOVA",
    description:
      "Reveals which parameters create the biggest performance swings. Most reliable for identifying parameters that dramatically separate winning from losing strategies. Use when you want to find the 'make-or-break' settings that matter most for profitability.",
    icon: <Brain className="h-4 w-4" />,
  },
  mdi: {
    label: "MDI",
    description:
      "Shows which parameters the optimization algorithm relied on most heavily during the search. Fast to compute but may overemphasize parameters that are easy to split on. Best for getting a quick sense of what drove the optimization decisions.",
    icon: <GitBranch className="h-4 w-4" />,
  },
  permutation: {
    label: "Permutation",
    description:
      "Tests parameter importance by scrambling values and measuring performance drops. Most trustworthy for real-world importance because it directly measures what happens when parameters go wrong. Use this when you need confidence in your parameter rankings.",
    icon: <Activity className="h-4 w-4" />,
  },
  hybrid: {
    label: "Hybrid",
    description:
      "Combines multiple importance calculation methods to provide a balanced view. Integrates correlation, variance contribution, and permutation importance for robust parameter ranking. Use this for the most comprehensive importance assessment.",
    icon: <TrendingUp className="h-4 w-4" />,
  },
  pruningAware: {
    label: "Pruning-Aware",
    description:
      "Accounts for trials that were stopped early due to poor performance. Essential when many trials were pruned, as it prevents overlooking parameters that might perform well if given more time. Use when your optimization had high pruning rates.",
    icon: <Filter className="h-4 w-4" />,
  },
};

const CHART_COLORS = {
  primary: "#3b82f6",
  secondary: "#10b981",
  tertiary: "#f59e0b",
  quaternary: "#ef4444",
  quinary: "#8b5cf6",
  senary: "#06b6d4",
};

// Helper function to safely convert to number and handle NaN/undefined
const safeNumber = (value: unknown, fallback: number = 0): number => {
  if (value === null || value === undefined) return fallback;
  const num = typeof value === "number" ? value : parseFloat(value as string);
  return isNaN(num) || !isFinite(num) ? fallback : num;
};

export default function ParameterImportanceEnhanced({
  results,
  analysis,
}: ParameterImportanceEnhancedProps) {
  const [selectedMethod, setSelectedMethod] =
    useState<ImportanceMethod>("fanova");
  const [showConfidenceIntervals, setShowConfidenceIntervals] = useState(true);
  const [showPruningAdjustment, setShowPruningAdjustment] = useState(true);




  useEffect(() => {
    logger.group("ParameterImportanceEnhanced.mount");
    logger.info("Input snapshot", {
      hasResults: !!results,
      hasAnalysis: !!analysis,
      nTrials: results?.n_trials,
      topTrials: results?.top_trials?.length,
      nCompleted: results?.n_completed_trials,
    });
    if (!results?.parameter_importance) {
      logger.warn(
        "No API-provided parameter_importance. Falling back to computed analysis only.",
      );
    }
    logger.groupEnd();
  }, [results, analysis]);

  // Calculate enhanced parameter importance with multiple methods
  const importance = results?.parameter_importance as
    | ParameterImportanceData
    | undefined;
  const hasAnyApiMethod =
    !!importance &&
    (["fanova", "mdi", "permutation", "hybrid", "pruning_aware"] as const).some(
      (k) => {
        const m = importance?.[k] as Record<string, number> | undefined;
        return m && Object.keys(m).length > 0;
      },
    );
  const hasApiMeta =
    importance?._meta?.source === "optuna_native" ||
    importance?._meta?.source === "optuna_enhanced";
  const hasLocalAnalysis = (analysis?.parameterImportance?.length ?? 0) > 0;
  const hasImportance =
    (!!importance && (hasApiMeta || hasAnyApiMethod)) || hasLocalAnalysis;

  // Set smart default method based on data source
  useEffect(() => {
    if (hasApiMeta && importance?._meta?.source === "optuna_native") {
      // Prefer fANOVA for Optuna native implementation (gold standard)
      setSelectedMethod("fanova");
    } else if (!hasAnyApiMethod && !hasApiMeta && hasLocalAnalysis) {
      // Use hybrid for fallback local analysis
      setSelectedMethod("hybrid");
    }
  }, [
    hasAnyApiMethod,
    hasApiMeta,
    hasLocalAnalysis,
    importance?._meta?.source,
  ]);

  const enhancedImportance = useMemo(() => {
    // Prefer API payload when present
    if (hasAnyApiMethod || hasApiMeta) {
      const api = results.parameter_importance as ParameterImportanceData;
      const maps = {
        fanova: api.fanova ?? {},
        mdi: api.mdi ?? {},
        permutation: api.permutation ?? {},
        hybrid: api.hybrid ?? {},
        pruning_aware: api.pruning_aware ?? {},
      };
      const paramNames = Array.from(
        new Set(
          Object.values(maps).flatMap((m: Record<string, number>) =>
            Object.keys(m || {}),
          ),
        ),
      ).filter((name) => {
        // Exclude boolean parameters
        if (isBooleanParameter(name)) return false;

        // Exclude minimum trades parameter
        const nameLower = name.toLowerCase();
        if (nameLower.includes('minimum') && nameLower.includes('trade')) {
          return false;
        }

        return true;
      });
      return paramNames.map((name) => {
        const ci: [number, number] | undefined =
          api?.confidence_intervals?.[name];
        const pruningStats = api?.pruning_stats?.[name];
        const interactionStrength = api?.interactions?.[name] ?? 0;
        return {
          name,
          displayName: getParameterDisplayName(name),
          fanova: Number(maps.fanova[name] ?? 0),
          mdi: Number(maps.mdi[name] ?? 0),
          permutation: Number(maps.permutation[name] ?? 0),
          hybrid: Number(maps.hybrid[name] ?? 0),
          pruningAware: Number(maps.pruning_aware[name] ?? 0),
          confidence: Math.min(
            1,
            Math.max(
              0,
              (results.n_completed_trials ?? results.n_trials_completed ?? 0) /
                100,
            ),
          ),
          confidenceInterval: ci ?? [0, 0],
          pruningRate: pruningStats?.pruning_rate ?? 0,
          convergenceScore: 0,
          interactionStrength: Number(interactionStrength) || 0,
          pruningStats,
        } as ImportanceRow;
      });
    }
    // Fallback: use locally computed analysis so charts render
    if (hasLocalAnalysis) {
      // Use local analysis without normalization
      return analysis!.parameterImportance
        .filter((p) => {
          // Exclude boolean parameters
          if (isBooleanParameter(p.name)) return false;

          // Exclude minimum trades parameter
          const nameLower = p.name.toLowerCase();
          if (nameLower.includes('minimum') && nameLower.includes('trade')) {
            return false;
          }

          return true;
        })
        .map((p) => ({
          name: p.name,
          displayName: getParameterDisplayName(p.name),
          // Use the properly calculated method scores from enhanced analysis
          fanova: Math.max(0, Math.min(1, ((p as any).fanovaScore || 0) / Math.max(...analysis!.parameterImportance.map((p: any) => p.fanovaScore || 0), 0.00001))),
          mdi: Math.max(0, Math.min(1, ((p as any).mdiScore || 0) / Math.max(...analysis!.parameterImportance.map((p: any) => p.mdiScore || 0), 0.00001))),
          permutation: Math.max(0, Math.min(1, ((p as any).permutationImportance || 0) / Math.max(...analysis!.parameterImportance.map((p: any) => p.permutationImportance || 0), 0.00001))),
          hybrid: Math.max(0, Math.min(1, ((p as any).hybridScore || p.importance) / Math.max(...analysis!.parameterImportance.map((p: any) => p.hybridScore || p.importance), 0.00001))),
          pruningAware: Math.max(0, Math.min(1, ((p as any).pruningAwareScore || 0) / Math.max(...analysis!.parameterImportance.map((p: any) => p.pruningAwareScore || 0), 0.00001))),
          confidence: Math.min(
            1,
            Math.max(
              0,
              (results?.n_completed_trials ??
                results?.n_trials_completed ??
                0) / 100,
            ),
          ),
          confidenceInterval: [0, 0] as [number, number],
          pruningRate: 0,
          convergenceScore: 0,
          interactionStrength: 0,
        }));
    }
    return [];
  }, [results, analysis, hasAnyApiMethod, hasApiMeta, hasLocalAnalysis]);

  useEffect(() => {
    logger.group("ParameterImportanceEnhanced.debugging");
    logger.info("Detection logic state", {
      hasImportance,
      hasApiMeta,
      hasAnyApiMethod,
      hasLocalAnalysis,
      importanceKeys: importance ? Object.keys(importance) : null,
      metaSource: importance?._meta?.source,
      enhancedImportanceCount: enhancedImportance.length,
      selectedMethod,
    });

    // Debug each method's data
    if (importance) {
      (["fanova", "mdi", "permutation", "hybrid", "pruning_aware"] as const).forEach(
        (method) => {
          const methodData = importance[method];
          logger.info(`Method ${method}`, {
            hasData: !!methodData,
            keyCount:
              methodData && typeof methodData === "object"
                ? Object.keys(methodData).length
                : 0,
            sampleKeys:
              methodData && typeof methodData === "object"
                ? Object.keys(methodData).slice(0, 3)
                : null,
          });
        },
      );
    }

    logger.groupEnd();
  }, [
    enhancedImportance,
    importance,
    hasImportance,
    hasApiMeta,
    hasAnyApiMethod,
    hasLocalAnalysis,
    selectedMethod,
  ]);

  // Sort parameters by selected method
  const sortedImportance = useMemo(() => {
    return [...enhancedImportance].sort(
      (a, b) => b[selectedMethod] - a[selectedMethod],
    );
  }, [enhancedImportance, selectedMethod]);

  // Prepare data for method comparison with smart normalization
  type MethodKey = "fanova" | "mdi" | "permutation" | "hybrid" | "pruningAware";
  const METHOD_KEYS: MethodKey[] = [
    "fanova",
    "mdi",
    "permutation",
    "hybrid",
    "pruningAware",
  ];

  // 1) Global maxima per method (across all parameters)
  const maxByMethod = useMemo(() => {
    const acc = {} as Record<MethodKey, number>;
    METHOD_KEYS.forEach((m) => {
      acc[m] = Math.max(...sortedImportance.map((r) => Number(r[m] || 0)), 0);
    });
    return acc;
  }, [sortedImportance]);

  // 2) Only show methods that actually have data
  const activeMethods = useMemo(
    () => METHOD_KEYS.filter((m) => (maxByMethod[m] ?? 0) > 1e-6),
    [maxByMethod],
  );

  const methodComparisonData = useMemo(() => {
    // Normalize per method globally so differences appear correctly
    const scale = (m: MethodKey, v: number) => {
      const d = maxByMethod[m] || 1;
      return d === 0 ? 0 : v / d; // 0..1 for that method across all parameters
    };

    const data = sortedImportance.slice(0, 10).map((param) => ({
      parameter: param.displayName,
      fANOVA: scale("fanova", param.fanova),
      MDI: scale("mdi", param.mdi),
      Permutation: scale("permutation", param.permutation),
      Hybrid: scale("hybrid", param.hybrid),
      "Pruning-Aware": scale("pruningAware", param.pruningAware),
    }));

    const hasNonZeroValues = data.some(
      (item) =>
        item.fANOVA > 0 ||
        item.MDI > 0 ||
        item.Permutation > 0 ||
        item.Hybrid > 0 ||
        item["Pruning-Aware"] > 0,
    );

    logger.info("Method comparison data prepared", {
      dataLength: data.length,
      sampleItem: data[0],
      hasNonZeroValues,
      activeMethods,
      maxByMethod,
      allItemsZero: data.every(
        (item) =>
          item.fANOVA === 0 &&
          item.MDI === 0 &&
          item.Permutation === 0 &&
          item.Hybrid === 0 &&
          item["Pruning-Aware"] === 0,
      ),
      sortedImportanceLength: sortedImportance.length,
      firstParamValues: sortedImportance[0]
        ? {
            name: sortedImportance[0].name,
            fanova: sortedImportance[0].fanova,
            mdi: sortedImportance[0].mdi,
            permutation: sortedImportance[0].permutation,
            pruningAware: sortedImportance[0].pruningAware,
          }
        : null,
    });

    return data;
  }, [sortedImportance, maxByMethod]);

  // Prepare radar chart data
  const radarData = useMemo(() => {
    return sortedImportance
      .filter((param) => {
        // Exclude boolean parameters
        if (isBooleanParameter(param.name)) return false;

        // Exclude minimum trades parameter
        const nameLower = param.name.toLowerCase();
        if (nameLower.includes('minimum') && nameLower.includes('trade')) {
          return false;
        }

        return true;
      })
      .slice(0, 8)
      .map((param) => ({
        parameter: param.displayName,
        importance: param[selectedMethod] * 100,
        confidence: param.confidence * 100,
        convergence: param.convergenceScore * 100,
      }));
  }, [sortedImportance, selectedMethod]);

  const renderImportanceBar = (importance: ImportanceRow) => {
    const value = safeNumber(importance[selectedMethod], 0);
    const percentage = Math.max(0, Math.min(100, value * 100));

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-medium">{importance.displayName}</span>
            {importance.pruningRate > 0.5 && showPruningAdjustment && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <AlertCircle
                      className="h-3 w-3 text-orange-500"
                      role="img"
                      aria-label="Warning indicator"
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      High pruning rate (
                      {(importance.pruningRate * 100).toFixed(1)}%)
                    </p>
                    <p className="text-xs">
                      May indicate challenging parameter region
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-mono">{percentage.toFixed(1)}%</span>
            {importance.confidence < 0.8 && (
              <Badge variant="outline" className="text-xs">
                Low confidence
              </Badge>
            )}
          </div>
        </div>
        <div className="relative">
          <Progress value={percentage} className="h-2" />
          {showConfidenceIntervals && selectedMethod === "fanova" && (
            <div
              className="absolute top-0 h-2 bg-primary/20 rounded"
              style={{
                left: `${importance.confidenceInterval[0] * 100}%`,
                width: `${(importance.confidenceInterval[1] - importance.confidenceInterval[0]) * 100}%`,
              }}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header with method selector */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">
            Enhanced Parameter Importance Analysis
          </h3>
          <p className="text-sm text-muted-foreground">
            Multiple methods for robust parameter importance estimation
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Button
              variant={showConfidenceIntervals ? "default" : "outline"}
              size="sm"
              onClick={() =>
                setShowConfidenceIntervals(!showConfidenceIntervals)
              }
              aria-label="Toggle confidence intervals display"
            >
              Confidence Intervals
            </Button>
            <Button
              variant={showPruningAdjustment ? "default" : "outline"}
              size="sm"
              onClick={() => setShowPruningAdjustment(!showPruningAdjustment)}
              aria-label="Toggle pruning adjustment display"
            >
              Pruning Adjustment
            </Button>
          </div>
          <Select
            value={selectedMethod}
            onValueChange={(v) => setSelectedMethod(v as ImportanceMethod)}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(IMPORTANCE_METHODS).map(([key, method]) => (
                <SelectItem key={key} value={key}>
                  <div className="flex items-center gap-2">
                    {method.icon}
                    <span>{method.label}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Method description */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-primary/10 rounded-lg">
              {IMPORTANCE_METHODS[selectedMethod].icon}
            </div>
            <div>
              <h4 className="font-medium">
                {IMPORTANCE_METHODS[selectedMethod].label}
              </h4>
              <p className="text-sm text-muted-foreground mt-1">
                {IMPORTANCE_METHODS[selectedMethod].description}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>



      {/* Parameter Importance Ranking */}
      <Card>
        <CardHeader>
          <CardTitle>Parameter Importance Ranking</CardTitle>
          <CardDescription>
            Parameters ranked by {IMPORTANCE_METHODS[selectedMethod].label}{" "}
            importance
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!hasImportance || enhancedImportance.length === 0 ? (
            <div className="h-[300px] flex items-center justify-center">
              <div className="text-center">
                <AlertCircle
                  className="h-12 w-12 text-muted-foreground mx-auto mb-4"
                  role="img"
                  aria-label="No data available indicator"
                />
                <p className="text-lg font-medium">No Data Available</p>
                <p className="text-sm text-muted-foreground">
                  Parameter importance data is not available for ranking.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedImportance
                .filter((param) => {
                  // Exclude boolean parameters
                  if (isBooleanParameter(param.name)) return false;

                  // Exclude minimum trades parameter
                  const nameLower = param.name.toLowerCase();
                  if (nameLower.includes('minimum') && nameLower.includes('trade')) {
                    return false;
                  }

                  return true;
                })
                .map((param, index) => (
                  <div key={param.name} className="space-y-2">
                    {renderImportanceBar(param)}
                    {index === 0 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Most influential parameter with{" "}
                        {(param[selectedMethod] * 100).toFixed(1)}%
                        importance
                      </p>
                    )}
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Secondary Analysis - Two-column grid for advanced charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Multi-Dimensional Parameter Analysis (Your Spider Plot Innovation) */}
        <Card>
          <CardHeader>
            <CardTitle>Multi-Dimensional Analysis</CardTitle>
            <CardDescription>
              Advanced radar visualization showing importance, confidence, and
              convergence relationships
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!hasImportance || enhancedImportance.length === 0 ? (
              <div className="h-[400px] flex items-center justify-center">
                <div className="text-center">
                  <AlertCircle
                    className="h-12 w-12 text-muted-foreground mx-auto mb-4"
                    role="img"
                    aria-label="No data available indicator"
                  />
                  <p className="text-lg font-medium">No Data Available</p>
                  <p className="text-sm text-muted-foreground">
                    Parameter importance data is not available for
                    multi-dimensional analysis.
                  </p>
                </div>
              </div>
            ) : (
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="parameter" />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} />
                    <Radar
                      name="Importance"
                      dataKey="importance"
                      stroke={CHART_COLORS.primary}
                      fill={CHART_COLORS.primary}
                      fillOpacity={0.6}
                    />
                    <Radar
                      name="Confidence"
                      dataKey="confidence"
                      stroke={CHART_COLORS.secondary}
                      fill={CHART_COLORS.secondary}
                      fillOpacity={0.4}
                    />
                    <Radar
                      name="Convergence"
                      dataKey="convergence"
                      stroke={CHART_COLORS.tertiary}
                      fill={CHART_COLORS.tertiary}
                      fillOpacity={0.3}
                    />
                    <Legend />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Method Comparison Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Method Comparison</CardTitle>
            <CardDescription>
              Compare importance rankings across different calculation methods
            </CardDescription>
          </CardHeader>
          <CardContent>
            {methodComparisonData.length === 0 ||
            !enhancedImportance ||
            !hasImportance ||
            enhancedImportance.length === 0 ? (
              <div className="h-[400px] flex items-center justify-center">
                <div className="text-center">
                  <AlertCircle
                    className="h-12 w-12 text-muted-foreground mx-auto mb-4"
                    role="img"
                    aria-label="No data available indicator"
                  />
                  <p className="text-lg font-medium">No Data Available</p>
                  <p className="text-sm text-muted-foreground">
                    Parameter importance data is not available for comparison.
                  </p>
                </div>
              </div>
            ) : (
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={methodComparisonData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#374151"
                      opacity={0.3}
                    />
                    <XAxis
                      dataKey="parameter"
                      stroke="#9CA3AF"
                      fontSize={11}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      domain={[0, 1]}
                      tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
                      stroke="#9CA3AF"
                    />
                    <RechartsTooltip
                      formatter={(value: number) => [
                        `${(value * 100).toFixed(1)}%`,
                        "Importance",
                      ]}
                      labelFormatter={(label) => `Parameter: ${label}`}
                      contentStyle={{
                        backgroundColor: "hsl(var(--background))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                        fontSize: "12px",
                      }}
                    />
                    <Legend
                      wrapperStyle={{ fontSize: "12px", paddingTop: "10px" }}
                    />
                    {activeMethods.includes("fanova") && (
                      <Bar
                        dataKey="fANOVA"
                        fill={CHART_COLORS.primary}
                        name="fANOVA"
                      />
                    )}
                    {activeMethods.includes("mdi") && (
                      <Bar
                        dataKey="MDI"
                        fill={CHART_COLORS.secondary}
                        name="MDI"
                      />
                    )}
                    {activeMethods.includes("permutation") && (
                      <Bar
                        dataKey="Permutation"
                        fill={CHART_COLORS.tertiary}
                        name="Permutation"
                      />
                    )}
                    {activeMethods.includes("hybrid") && (
                      <Bar
                        dataKey="Hybrid"
                        fill={CHART_COLORS.quaternary}
                        name="Hybrid"
                      />
                    )}
                    {activeMethods.includes("pruningAware") && (
                      <Bar
                        dataKey="Pruning-Aware"
                        fill={CHART_COLORS.quinary}
                        name="Pruning-Aware"
                      />
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Parameter Interactions */}
      <Card>
        <CardHeader>
          <CardTitle>Parameter Interaction Effects</CardTitle>
          <CardDescription>
            Estimated interaction strength between parameters (beta feature)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {sortedImportance
              .filter((param) => {
                // Exclude boolean parameters
                if (isBooleanParameter(param.name)) return false;

                // Exclude minimum trades parameter
                const nameLower = param.name.toLowerCase();
                if (nameLower.includes('minimum') && nameLower.includes('trade')) {
                  return false;
                }

                return true;
              })
              .slice(0, 5)
              .map((param) => (
                <div key={param.name} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">{param.displayName}</h4>
                    <Badge variant="outline">
                      {(param.interactionStrength * 100).toFixed(1)}%
                      interaction
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {param.interactionStrength > 0.2
                      ? "Strong interactions detected with other parameters"
                      : "Mostly independent effect on performance"}
                  </p>
                </div>
              ))}
          </div>
          <div className="mt-4 p-4 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> Interaction effects are estimated using
              fANOVA's second-order terms. High interaction strength
              suggests parameters should be tuned together.
            </p>
          </div>
        </CardContent>
      </Card>


      {/* Insights and recommendations */}
      <Card>
        <CardHeader>
          <CardTitle>Importance-Based Insights</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <TrendingUp className="h-5 w-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium">Top Parameters</p>
                <p className="text-sm text-muted-foreground">
                  Focus optimization efforts on{" "}
                  {sortedImportance
                    .filter((p) => {
                      // Exclude boolean parameters
                      if (isBooleanParameter(p.name)) return false;

                      // Exclude minimum trades parameter
                      const nameLower = p.name.toLowerCase();
                      if (nameLower.includes('minimum') && nameLower.includes('trade')) {
                        return false;
                      }

                      return true;
                    })
                    .slice(0, 3)
                    .map((p) => p.displayName)
                    .join(", ")}
                  which show the highest impact on performance.
                </p>
              </div>
            </div>
            {(() => {
              const trials = results?.n_trials ?? 0;
              const pruned = results?.n_pruned_trials ?? 0;
              const prunedPct = trials > 0 ? pruned / trials : 0;
              return prunedPct > 0.5 ? (
                <div className="flex items-start gap-3">
                  <AlertCircle
                    className="h-5 w-5 text-orange-500 mt-0.5"
                    role="img"
                    aria-label="High pruning rate warning"
                  />
                  <div>
                    <p className="font-medium">High Pruning Rate</p>
                    <p className="text-sm text-muted-foreground">
                      {(prunedPct * 100).toFixed(1)}% of trials were pruned.
                      Consider using pruning-aware importance to account for
                      bias.
                    </p>
                  </div>
                </div>
              ) : null;
            })()}
            {(sortedImportance[0]?.confidence ?? 1) < 0.8 && (
              <div className="flex items-start gap-3">
                <Info
                  className="h-5 w-5 text-blue-500 mt-0.5"
                  role="img"
                  aria-label="Low confidence information"
                />
                <div>
                  <p className="font-medium">Low Confidence</p>
                  <p className="text-sm text-muted-foreground">
                    Importance estimates have low confidence. Consider running
                    more trials for reliable rankings.
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
